import { useEffect, useRef, useCallback, useState } from 'react';

const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'wheel'];

/**
 * Signs the user out after a period of inactivity, showing a warning
 * countdown for `warningMs` before the sign-out actually happens.
 */
export default function useInactivityLogout(signOut, timeoutMs = 15 * 60 * 1000, warningMs = 30 * 1000) {
    const logoutTimerRef = useRef(null);
    const warningTimerRef = useRef(null);
    const intervalRef = useRef(null);
    const showWarningRef = useRef(false);
    const [showWarning, setShowWarning] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState(Math.round(warningMs / 1000));

    const clearAllTimers = useCallback(() => {
        clearTimeout(logoutTimerRef.current);
        clearTimeout(warningTimerRef.current);
        clearInterval(intervalRef.current);
    }, []);

    const startWarningCountdown = useCallback(() => {
        showWarningRef.current = true;
        setShowWarning(true);
        setSecondsLeft(Math.round(warningMs / 1000));

        intervalRef.current = setInterval(() => {
            setSecondsLeft((prev) => (prev > 0 ? prev - 1 : 0));
        }, 1000);

        logoutTimerRef.current = setTimeout(() => {
            clearAllTimers();
            showWarningRef.current = false;
            setShowWarning(false);
            signOut && signOut();
        }, warningMs);
    }, [warningMs, signOut, clearAllTimers]);

    const resetTimer = useCallback(() => {
        if (showWarningRef.current) return; // ignore background activity while the dialog is open
        clearAllTimers();
        warningTimerRef.current = setTimeout(startWarningCountdown, timeoutMs - warningMs);
    }, [clearAllTimers, startWarningCountdown, timeoutMs, warningMs]);

    const stayActive = useCallback(() => {
        clearAllTimers();
        showWarningRef.current = false;
        setShowWarning(false);
        resetTimer();
    }, [clearAllTimers, resetTimer]);

    useEffect(() => {
        if (!signOut) return undefined;

        resetTimer();
        ACTIVITY_EVENTS.forEach((event) => window.addEventListener(event, resetTimer));

        return () => {
            clearAllTimers();
            ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, resetTimer));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signOut, timeoutMs, warningMs]);

    return { showWarning, secondsLeft, stayActive };
}
